import { EllipsisWithTooltip } from '@/components/basicComponents/ellipsisWithTooltip';
import Tag from '@/components/basicComponents/tag';
import FormattingTooltip from '@/components/basicComponents/tooltip/dollarsTooltip';
import { decimalToPercentage } from '@/components/dueDiligence-page/dueDiligenceRecord/utils';
import { Loader } from '@/components/loaders/loader';
import { PublishedInvestmentManagerUpdatesDto, PublishedInvestmentManagerUpdatesSourceEnum } from '@/components/managerUpdates-page/types';
import { getTagStyleType } from '@/components/managerUpdates-page/utils';
import NoResults from '@/components/table/noResults';
import RedirectTitleCell from '@/components/table/redirectTitleCell';
import {
  TableBodyWrapper,
  TableFixedCell,
  TableRowCell,
  TableScrollableCellsWrapper,
  TableWithHorizontalScroll,
  TableWithHorizontalScrollRowWrapper,
  TableWithHorizontalScrollWrapper,
  TextCell
} from '@/components/table/styles';
import TableHeader from '@/components/table/tableHeader';
import { ISort, TableColumns } from '@/components/table/types';
import { useResponsive } from '@/hooks/use-responsive';
import { dateFormatter } from '@/shared/utils/date';
import { getEnumKeyByValue } from '@/shared/utils/enums';
import { themeVar } from '@/styles/theme';
import { headingsPlugin, linkDialogPlugin, linkPlugin, listsPlugin, MDXEditor, tablePlugin, thematicBreakPlugin } from '@mdxeditor/editor';
import { AIFieldType, InvestmentPacingType } from '@trace-8/trace8-schema';
import { format } from 'date-fns';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

export const getPublishedUpdatesTableCellValue = (type: string, value: string, commentary: string): JSX.Element => {
  if (!value && !commentary) {
    return (
      <CustomCell>
        <TextCell>--</TextCell>
      </CustomCell>
    );
  }

  switch (type) {
    case getEnumKeyByValue(AIFieldType, AIFieldType.LONG_FORM_CONTENT):
      const mdxPlugins = [listsPlugin(), headingsPlugin(), linkPlugin(), linkDialogPlugin(), tablePlugin(), thematicBreakPlugin()];
      const mdxEditorContent = (
        <CustomMDXEditor
          markdown={value || '--'}
          readOnly
          suppressHtmlProcessing={true}
          plugins={mdxPlugins}
          onError={(error) => console.log(error)}
        />
      );

      return (
        <CustomCell>
          <EllipsisWithTooltip title={mdxEditorContent} maxContentWidth={680} lines={4}>
            <TextCell>{mdxEditorContent}</TextCell>
          </EllipsisWithTooltip>
        </CustomCell>
      );
    case getEnumKeyByValue(AIFieldType, AIFieldType.SHORT_TEXT):
      return (
        <CustomCell>
          <EllipsisWithTooltip title={value || '--'} maxContentWidth={680} lines={4}>
            <TextCell>{value || '--'}</TextCell>
          </EllipsisWithTooltip>
        </CustomCell>
      );
    case getEnumKeyByValue(AIFieldType, AIFieldType.NUMBER):
      return (
        <CustomCell>
          {!!value && <TextCell>{value}</TextCell>}
          {!!commentary && (
            <EllipsisWithTooltip title={commentary || '--'} maxContentWidth={680} lines={4}>
              <TextCell>{commentary || '--'}</TextCell>
            </EllipsisWithTooltip>
          )}
        </CustomCell>
      );
    case getEnumKeyByValue(AIFieldType, AIFieldType.DOLLAR):
      return (
        <CustomCell>
          {!!value && (
            <TextCell>
              <FormattingTooltip>{+value}</FormattingTooltip>
            </TextCell>
          )}
          {!!commentary && (
            <EllipsisWithTooltip title={commentary || '--'} maxContentWidth={680} lines={4}>
              <TextCell>{commentary || '--'}</TextCell>
            </EllipsisWithTooltip>
          )}
        </CustomCell>
      );
    case getEnumKeyByValue(AIFieldType, AIFieldType.PERCENTAGE):
      return (
        <CustomCell>
          {!!value && <TextCell>{decimalToPercentage(value)}</TextCell>}
          {!!commentary && (
            <EllipsisWithTooltip title={commentary || '--'} maxContentWidth={680} lines={4}>
              <TextCell>{commentary || '--'}</TextCell>
            </EllipsisWithTooltip>
          )}
        </CustomCell>
      );
    case getEnumKeyByValue(AIFieldType, AIFieldType.DATE):
      return (
        <CustomCell>
          {!!value && <TextCell>{dateFormatter(format(new Date(value), 'yyyy-MM-dd'), 'yyyy-MM-dd')}</TextCell>}
          {!!commentary && (
            <EllipsisWithTooltip title={commentary || '--'} maxContentWidth={680} lines={4}>
              <TextCell>{commentary || '--'}</TextCell>
            </EllipsisWithTooltip>
          )}
        </CustomCell>
      );
    default:
      return (
        <CustomCell>
          <TextCell>--</TextCell>
        </CustomCell>
      );
  }
};

export const getPublishedUpdatesTableAssetClassValue = (investment: PublishedInvestmentManagerUpdatesDto['investment']) => {
  if (investment?.subAssetClass) {
    return `${investment?.assetClass?.name} - ${investment?.subAssetClass?.name}`;
  }

  return investment?.assetClass?.name;
};

export const getPublishedUpdatesTablePacingValue = (investment: PublishedInvestmentManagerUpdatesDto['investment'], t: TFunction) => {
  if (investment?.pacingType === getEnumKeyByValue(InvestmentPacingType, InvestmentPacingType.DRAWDOWN)) {
    return `${investment?.pacingStartingYear}`;
  }

  return t(`enums:InvestmentPacingType.${investment?.pacingType}`);
};

interface PublishedUpdatesTableProps {
  columns: TableColumns[];
  publishedUpdates: PublishedInvestmentManagerUpdatesDto[];
  sort: ISort;
  loading: boolean;
  source: PublishedInvestmentManagerUpdatesSourceEnum;
  setSort: (value: ISort) => void;
  openOpportunityDetailsPage: (investmentId: string) => void;
}

export const PublishedUpdatesTable = ({
  columns,
  publishedUpdates,
  sort,
  loading,
  source,
  setSort,
  openOpportunityDetailsPage
}: PublishedUpdatesTableProps) => {
  const { isTablet } = useResponsive();

  const { t } = useTranslation('enums');

  // 5 is the gap (spacing) between table cells
  const totalTableWidth = columns.reduce((acc, column) => acc + column.width + 5, 0);

  return (
    <TableWithHorizontalScrollWrapper isTablet={isTablet}>
      <TableWithHorizontalScroll totalWidth={`${totalTableWidth}px`}>
        <TableHeader refetch={() => {}} columns={columns} savedSort={sort} savedSetSort={setSort} withHorizontalScroll />
        <TableBodyWrapper>
          {loading ? (
            <Loader />
          ) : (
            <>
              {!publishedUpdates.length ? (
                <NoResults />
              ) : (
                <>
                  {publishedUpdates.map((row, index) => {
                    const { investment, legalEntityCommittedCapital } = row || {};
                    const assetClassValue = getPublishedUpdatesTableAssetClassValue(investment);
                    const pacingValue = getPublishedUpdatesTablePacingValue(investment, t);

                    return (
                      <TableWithHorizontalScrollRowWrapper key={investment?.id ?? index}>
                        <TableFixedCell id="fixedCell" width={columns[0].width}>
                          <CustomCell>
                            <AssetClass>{`${assetClassValue} (${pacingValue})`}</AssetClass>
                            <RedirectTitleCell id="actionCurioText" onClick={() => openOpportunityDetailsPage(investment?.id)}>
                              <StrategyName>{investment?.name}</StrategyName>
                            </RedirectTitleCell>
                            {source === PublishedInvestmentManagerUpdatesSourceEnum.UPDATES_TAB && (
                              <>
                                {legalEntityCommittedCapital?.map((legalEntityCommittedCapitalItem) => {
                                  const { entityName, committedCapital } = legalEntityCommittedCapitalItem || {};

                                  return (
                                    <CustomTextCell key={entityName + committedCapital}>
                                      {entityName}: <FormattingTooltip>{committedCapital}</FormattingTooltip>
                                    </CustomTextCell>
                                  );
                                })}
                              </>
                            )}
                          </CustomCell>
                        </TableFixedCell>
                        <TableScrollableCellsWrapper>
                          {row.publishedUpdates.map((update, index) => (
                            <TableRowCell
                              key={`${row?.investment?.id ?? index}-${update?.documentField?.id}`}
                              width={columns[index + 1].width}
                              withHorizontalScroll
                            >
                              {getPublishedUpdatesTableCellValue(
                                update.documentField.type,
                                update?.documentFieldAnswer?.value,
                                update?.documentFieldAnswer?.commentary
                              )}
                            </TableRowCell>
                          ))}
                          <TableRowCell width={columns[columns.length - 1].width} withHorizontalScroll>
                            <CustomCell>
                              <TextCell>
                                {!!row?.lastUpdate ? dateFormatter(format(new Date(row?.lastUpdate), 'yyyy-MM-dd'), 'yyyy-MM-dd') : '--'}
                              </TextCell>
                              {row?.sentimentFlag && row?.sentimentFlagLabel && (
                                <Tag title={row?.sentimentFlagLabel} size="md" styleType={getTagStyleType(row?.sentimentFlag)} />
                              )}
                            </CustomCell>
                          </TableRowCell>
                        </TableScrollableCellsWrapper>
                      </TableWithHorizontalScrollRowWrapper>
                    );
                  })}
                </>
              )}
            </>
          )}
        </TableBodyWrapper>
      </TableWithHorizontalScroll>
    </TableWithHorizontalScrollWrapper>
  );
};

const CustomMDXEditor = styled(MDXEditor)`
  font-family: Blinker;
  font-size: 16px;
  .mdxeditor-root-contenteditable div {
    padding: 0;
  }
  .mdxeditor-root-contenteditable ul,
  .mdxeditor-root-contenteditable ol {
    padding-left: 26px;
  }
`;

const CustomCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const AssetClass = styled.p`
  font-size: 18px;
  font-weight: 400;
  line-height: 22.4px;
  padding: 0 4px;
  color: ${themeVar('font.weak')};
`;

const StrategyName = styled.p`
  font-size: 18px;
  font-weight: 600;
  line-height: 22.4px;
  padding: 0 4px;
`;

const CustomTextCell = styled.div`
  font-size: 18px;
  font-weight: 400;
  line-height: 22.4px;
  padding: 0 4px;
`;
